import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle, Phone, Mail } from "lucide-react";

import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";

export default function ServiceDetailPage() {
  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: false, // allow animations every time on scroll
    });
  }, []);

  const serviceData = {
    "building-renovation": {
      title: "Building Renovation",
      description:
        "Transform your existing space with our comprehensive renovation services",
      longDescription:
        "Our building renovation services breathe new life into existing structures. Whether you're looking to update a single room or completely transform your entire property, our experienced team handles every aspect of the renovation process with precision and care.",
      features: [
        "Complete home renovations",
        "Kitchen & bathroom remodeling",
        "Commercial space upgrades",
        "Structural modifications",
        "Energy efficiency improvements",
        "Historic building restoration",
      ],
      image:
        "https://i.pinimg.com/736x/6a/5f/94/6a5f9423f4d61bbf677e7d057c50713f.jpg",
      process: [
        "Initial consultation and assessment",
        "Design and planning phase",
        "Permit acquisition",
        "Construction and renovation",
        "Quality inspection and finishing",
        "Final walkthrough and handover",
      ],
      images: [
        "https://i.pinimg.com/736x/1f/33/cc/1f33ccb3165a1123685d8474b3e1052c.jpg",
        "https://i.pinimg.com/736x/27/e6/74/27e6742b4be4edec9fe2cab6242fcfb3.jpg",
        "https://i.pinimg.com/736x/9a/8a/e4/9a8ae416a93cded9ae015811d96bfc60.jpg",
      ],
    },
    "civil-contract": {
      title: "Civil Contract",
      description: "Professional civil engineering and construction contracts",
      image:
        "https://i.pinimg.com/736x/f8/ee/89/f8ee893349310b1417cbaf4e1bf7feac.jpg",
      longDescription:
        "Our civil contract services encompass large-scale infrastructure projects and construction contracts. We manage complex projects from conception to completion, ensuring adherence to all safety standards and regulations.",
      features: [
        "Infrastructure development",
        "Road construction",
        "Bridge building",
        "Site preparation",
        "Drainage systems",
        "Public works projects",
      ],
      process: [
        "Project feasibility study",
        "Engineering design and planning",
        "Environmental impact assessment",
        "Contract negotiation",
        "Construction management",
        "Project completion and handover",
      ],
      images: [
        "https://i.pinimg.com/736x/a0/36/6e/a0366e569008eabcd9fff9ad086c3df8.jpg",
        "https://i.pinimg.com/736x/3a/74/70/3a74703b1d791c6288d965b50515d1be.jpg",
        "https://i.pinimg.com/736x/27/56/fe/2756fe4138e2be83119452fb4c1f51ca.jpg",
      ],
    },
    architecture: {
      title: "Architecture",
      description: "Innovative architectural design and planning services",
      longDescription:
        "Our architectural services combine creativity with functionality to create spaces that inspire and serve their purpose. We specialize in sustainable design solutions that are both beautiful and environmentally responsible.",
      features: [
        "Residential design",
        "Commercial architecture",
        "3D modeling & visualization",
        "Sustainable design solutions",
        "Building information modeling (BIM)",
        "Construction documentation",
      ],
      image:
        "https://i.pinimg.com/736x/43/8a/ba/438aba4486283fa13711f0f4d3f53fda.jpg",
      process: [
        "Client consultation and brief",
        "Concept design development",
        "Design development and refinement",
        "Technical documentation",
        "Planning permission support",
        "Construction administration",
      ],
      images: [
        "https://i.pinimg.com/736x/59/0f/4b/590f4b0d933cb59248c84a26802c6a7e.jpg",
        "https://i.pinimg.com/736x/01/f9/88/01f9887444b0045d28a75688235f9a5c.jpg",
        "https://i.pinimg.com/736x/ab/c2/9f/abc29fbb3313984994c6787622c04494.jpg",
      ],
    },
    "interior-design": {
      title: "Interior Design",
      description: "Modern and functional interior design solutions",
      longDescription:
        "Our interior design services create beautiful, functional spaces that reflect your personality and lifestyle. We work closely with clients to understand their needs and preferences, delivering designs that exceed expectations.",
      features: [
        "Space planning and layout",
        "Color consultation",
        "Furniture selection and procurement",
        "Lighting design",
        "Material and finish selection",
        "Custom millwork design",
      ],
      image:
        "https://i.pinimg.com/736x/f3/33/cf/f333cf2f007999c913120812752bb37b.jpg",
      process: [
        "Initial consultation and space assessment",
        "Concept development and mood boards",
        "Design development and 3D visualization",
        "Material and furniture selection",
        "Implementation and installation",
        "Final styling and completion",
      ],
      images: [
        "https://i.pinimg.com/736x/af/01/bd/af01bddb705257d6babb1df1605c1035.jpg",
        "https://i.pinimg.com/736x/5e/3f/35/5e3f354974abf59da509ffa26985eb41.jpg",
        "https://i.pinimg.com/736x/5e/3f/35/5e3f354974abf59da509ffa26985eb41.jpg",
      ],
    },
    "tiles-installation": {
      title: "Tiles Installation",
      description: "Professional tile installation for all surfaces",
      longDescription:
        "Our tile installation services cover all types of tiling projects, from simple bathroom updates to complex commercial installations. We work with various materials and ensure perfect alignment and finishing.",
      features: [
        "Ceramic & porcelain tiles",
        "Natural stone installation",
        "Bathroom & kitchen tiling",
        "Outdoor tile work",
        "Mosaic and decorative tiling",
        "Waterproofing and sealing",
      ],
      image:
        "https://i.pinimg.com/736x/41/d0/6f/41d06f320c082d97cd8d70d07b74754c.jpg",
      process: [
        "Surface preparation and assessment",
        "Material selection and ordering",
        "Layout planning and marking",
        "Tile cutting and installation",
        "Grouting and sealing",
        "Final cleaning and inspection",
      ],
      images: [
        "https://i.pinimg.com/736x/91/d8/b6/91d8b60af1554edf83b333b732aa2a78.jpg",
        "https://i.pinimg.com/736x/81/ee/04/81ee04f29be0a730ab345ea56ed997d9.jpg",
        "https://i.pinimg.com/736x/35/8a/36/358a36759c1579502f12dda0f52b781d.jpg",
      ],
    },
    plumbing: {
      title: "Plumbing",
      image:
        "https://i.pinimg.com/736x/64/48/ac/6448ac9127092a01045daf088ece959d.jpg",

      description: "Complete plumbing solutions and maintenance services",
      longDescription:
        "Our plumbing services ensure your water systems function efficiently and reliably. From routine maintenance to emergency repairs and new installations, our certified plumbers deliver quality workmanship.",
      features: [
        "Pipe installation & repair",
        "Fixture installation",
        "Water heater services",
        "Emergency plumbing",
        "Drain cleaning and maintenance",
        "Water pressure optimization",
      ],
      process: [
        "Problem diagnosis and assessment",
        "Solution planning and estimation",
        "Material procurement",
        "Installation or repair work",
        "System testing and verification",
        "Maintenance recommendations",
      ],
      images: [
        "https://i.pinimg.com/736x/c0/fc/a9/c0fca9f9fb740c5f3538a438de5d7432.jpg",
        "https://i.pinimg.com/736x/5b/79/11/5b7911765a2ad889debb658c04759c98.jpg",
        "https://i.pinimg.com/736x/32/97/eb/3297ebb6c7dee81d78c59e00d9fd0260.jpg",
      ],
    },
  };

  const { service } = useParams();
  const navigate = useNavigate();
  const serviceDetail = serviceData[service];

  if (!serviceDetail) {
    navigate("/404"); // or render a 404 message
    return null;
  }

  // return (

  //   <div className="min-h-screen py-20 bg-white overflow-hidden">
  //     <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
  //       {/* Header */}
  //       <div className="text-center mb-16" data-aos="fade-down">
  //         <h1 className="text-5xl font-bold text-gray-900 mb-6">
  //           {serviceDetail.title}
  //         </h1>
  //         <p className="text-xl text-gray-600 max-w-3xl mx-auto">
  //           {serviceDetail.description}
  //         </p>
  //       </div>

  //       {/* Main Content */}
  //       <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
  //         {/* Left: About + Images */}
  //         <div className="lg:col-span-2" data-aos="fade-right">
  //           <div className="mb-8">
  //             <h2 className="text-3xl font-bold text-gray-900 mb-4">
  //               About This Service
  //             </h2>
  //             <p className="text-gray-600 text-lg leading-relaxed">
  //               {serviceDetail.longDescription}
  //             </p>
  //           </div>

  //           {/* Image Gallery */}

  //           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 cursor-pointer">
  //             {serviceDetail.images.map((image, index) => (
  //               <div
  //                 key={index}
  //                 className="overflow-hidden rounded-lg shadow-md"
  //                 data-aos="zoom-in"
  //                 data-aos-delay={index * 100}
  //               >
  //                 <img
  //                   src={image || "/placeholder.svg"}
  //                   alt={`${serviceDetail.title} ${index + 1}`}
  //                   className="w-full h-48 object-cover transition-transform duration-500 ease-in-out hover:scale-110"
  //                 />
  //               </div>
  //             ))}
  //           </div>
  //         </div>

  //         {/* Right Sidebar */}
  //         <div className="space-y-8">
  //           {/* Features Card */}
  //           <div
  //             className="bg-white rounded-lg shadow p-6"
  //             data-aos="fade-left"
  //           >
  //             <h3 className="text-xl font-semibold mb-4">What We Offer</h3>
  //             <ul className="space-y-3">
  //               {serviceDetail.features.map((feature, index) => (
  //                 <li key={index} className="flex items-start">
  //                   <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-1" />
  //                   <span className="text-gray-700">{feature}</span>
  //                 </li>
  //               ))}
  //             </ul>
  //           </div>

  //           {/* Contact Card */}
  //           <div
  //             className="bg-white rounded-lg shadow p-6"
  //             data-aos="fade-left"
  //             data-aos-delay="200"
  //           >
  //             <h3 className="text-xl font-semibold mb-2">Get Started Today</h3>
  //             <p className="text-sm text-gray-500 mb-4">
  //               Ready to begin your project? Contact us for a free consultation.
  //             </p>
  //             <a
  //               href="/contact"
  //               className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center font-semibold py-2 rounded"
  //             >
  //               Get Free Quote
  //             </a>
  //             <div className="flex items-center text-gray-600 mt-4">
  //               <Phone className="h-4 w-4 mr-2" />
  //               <span>(555) 123-4567</span>
  //             </div>
  //             <div className="flex items-center text-gray-600 mt-2">
  //               <Mail className="h-4 w-4 mr-2" />
  //               <span>info@Sanctus property pvt ltd.com</span>
  //             </div>
  //           </div>
  //         </div>
  //       </div>

  //       {/* Process Section */}

  //       <section className="mb-16" data-aos="fade-up">
  //         <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
  //           Our Process
  //         </h2>
  //         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  //           {serviceDetail.process.map((step, index) => (
  //             <div
  //               key={index}
  //               className="bg-gray-50 text-center rounded-lg shadow p-6 transition-all duration-300 hover:bg-gray-100 hover:shadow-xl cursor-pointer"
  //               data-aos="flip-up"
  //               data-aos-delay={index * 100}
  //             >
  //               <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
  //                 {index + 1}
  //               </div>
  //               <h4 className="text-lg font-medium text-gray-800">{step}</h4>
  //             </div>
  //           ))}
  //         </div>
  //       </section>

  //       {/* CTA Section */}
  //       <section
  //         className="text-center bg-blue-50 rounded-lg p-12"
  //         data-aos="zoom-in-up"
  //       >
  //         <h2 className="text-3xl font-bold text-gray-900 mb-4">
  //           Ready to Get Started?
  //         </h2>
  //         <p className="text-xl text-gray-600 mb-8">
  //           Contact us today to discuss your {serviceDetail.title.toLowerCase()}{" "}
  //           project
  //         </p>
  //         <div className="space-x-4">
  //           <Link
  //             to="/contact"
  //             className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded"
  //           >
  //             Contact Us
  //           </Link>

  //           <Link
  //             to="/projects"
  //             className="inline-block border border-blue-600 text-blue-600 hover:bg-blue-100 font-semibold py-2 px-6 rounded"
  //           >
  //             View Our Work
  //           </Link>
  //         </div>
  //       </section>
  //     </div>
  //   </div>
  // );
  return (
    <div className="overflow-hidden">
      {/* Header Section with Full-Width Background Image */}
      <section
        className="relative h-72 md:h-96 flex items-center justify-center bg-center bg-cover"
        style={{
          backgroundImage: `url(${
            serviceDetail.image || "/images/default-header.png"
          })`,
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black opacity-50"></div>

        {/* Text Content */}
        <div className="text-center mb-16">
          <h1
            className="text-5xl font-bold text-white mb-6"
            data-aos="fade-right"
          >
            {serviceDetail.title}
          </h1>
          <p
            className="text-xl text-white max-w-3xl mx-auto"
            data-aos="fade-left"
          >
            {serviceDetail.description}
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <div className="min-h-screen py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header Title & Description */}

          {/* Main Grid Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 mb-16">
            {/* Left Side */}
            <div className="lg:col-span-2" data-aos="fade-right">
              <div className="mb-8">
                <div className="inline-block">
                  <h2 className="text-3xl font-bold text-blue-700 mb-2">
                    About This Service
                  </h2>
                  <div className="h-1 w-40 bg-[#6FEF34] mx-auto mb-4 blink-underline"></div>
                </div>

                <p className="text-gray-600 text-lg leading-relaxed">
                  {serviceDetail.longDescription}
                </p>
              </div>

              {/* Image Gallery */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8 cursor-pointer">
                {serviceDetail.images.map((image, index) => (
                  <div
                    key={index}
                    className="overflow-hidden rounded-lg shadow-md"
                    data-aos="zoom-in"
                    data-aos-delay={index * 100}
                  >
                    <img
                      src={image || "/placeholder.svg"}
                      alt={`${serviceDetail.title} ${index + 1}`}
                      className="w-full h-48 object-cover transition-transform duration-500 ease-in-out hover:scale-110"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="space-y-8">
              {/* Features */}
              <div
                className="bg-white rounded-lg shadow p-6"
                data-aos="fade-left"
              >
                {/* <h3 className="text-xl font-semibold mb-4">What We Offer</h3> */}
                <div className="inline-block">
                  <h3 className="text-xl font-semibold mb-2 text-blue-700">
                    What We Offer
                  </h3>
                  <div className="h-1 w-24 bg-[#6FEF34] mx-auto mb-4 blink-underline"></div>
                </div>

                <ul className="space-y-3">
                  {serviceDetail.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-1" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Contact */}
              <div
                className="bg-white rounded-lg shadow p-6"
                data-aos="fade-left"
                data-aos-delay="200"
              >
                {/* <h3 className="text-xl font-semibold mb-2">
                  Get Started Today
                </h3> */}
                <div className="inline-block">
                  <h3 className="text-xl font-semibold mb-2 text-blue-700">
                    Get Started Today
                  </h3>
                  <div className="h-1 w-24 bg-[#6FEF34] mx-auto mb-4 blink-underline"></div>
                </div>

                <p className="text-sm text-gray-500 mb-4">
                  Ready to begin your project? Contact us for a free
                  consultation.
                </p>
                <a
                  href="/contact"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center font-semibold py-2 rounded"
                >
                  Get Free Quote
                </a>
                <div className="flex items-center text-gray-600 mt-4">
                  <Phone className="h-4 w-4 mr-2" />
                  <span>(555) 123-4567</span>
                </div>
                <div className="flex items-center text-gray-600 mt-2">
                  <Mail className="h-4 w-4 mr-2" />
                  <span>info@sanctusproperty.com</span>
                </div>
              </div>
            </div>
          </div>

          {/* Process Section */}
          <section className="mb-16" data-aos="fade-up">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-blue-700 mb-2">
                Our Process
              </h2>
              <div className="h-1 w-20 bg-[#6FEF34] mx-auto mb-6 blink-underline"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ">
              {serviceDetail.process.map((step, index) => (
                <div
                  key={index}
                  className="bg-gray-50 text-center rounded-lg shadow p-6 transition-all duration-300 hover:bg-gray-100 hover:shadow-xl cursor-pointer "
                  data-aos="flip-up"
                  data-aos-delay={index * 100}
                >
                  <div className="w-12 h-12 bg-[#6FEF34] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {index + 1}
                  </div>
                  <h4 className="text-lg font-medium text-blue-700">{step}</h4>
                </div>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section
            className="text-center bg-blue-50 rounded-lg p-12"
            data-aos="zoom-in-up"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Contact us today to discuss your{" "}
              {serviceDetail.title.toLowerCase()} project
            </p>
            <div className="space-x-4">
              <Link
                to="/contact"
                className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded"
              >
                Contact Us
              </Link>
              <Link
                to="/projects"
                className="inline-block border border-blue-600 text-blue-600 hover:bg-blue-100 font-semibold py-2 px-6 rounded"
              >
                View Our Work
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
